/* SmartHuntsRow — Sprint 1 (3 quickstart buttons)
 *   • Look-alike bedrijven vanuit gewonnen deals    (POST /quickstart/look-alike)
 *   • Buyer-intent scanner (2+ triggers)             (POST /quickstart/buyer-intent)
 *   • Competitor-churn signaal                       (POST /quickstart/competitor-churn)
 *
 * Elke tile fires zijn eigen endpoint, streamt naar Signals list en toast'et
 * het resultaat. De hele row is een "proactieve hunt-launcher" — 1 klik en de
 * agent gaat aan het werk.
 * Geëxtraheerd uit Signals.jsx (refactor).
 */
import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import { Sparkles, Loader2, ArrowUpRight } from "lucide-react";

export function SmartHuntsRow({ onRun }) {
    const [running, setRunning] = useState(null);   // "look_alike" | "buyer_intent" | "competitor_churn" | null
    const [showChurn, setShowChurn] = useState(false);
    const [churnInput, setChurnInput] = useState("");
    const [showIntent, setShowIntent] = useState(false);
    const [intentForm, setIntentForm] = useState({ target_sector: "", target_country: "", target_role: "" });
    const [source, setSource] = useState("perplexity");  // "perplexity" | "serper"

    const trigger = async (endpoint, body, label) => {
        setRunning(endpoint);
        try {
            const r = await api.post(`/hunt-missions/quickstart/${endpoint}`, { ...body, source });
            const n = r.data?.first_run?.signals_generated ?? 0;
            const title = r.data?.mission?.title || label;
            toast.success(i18n.t("smarthunts.done", { title, count: n, source }));
            onRun?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("smarthunts.failed", { label }));
        } finally {
            setRunning(null);
            setShowChurn(false); setShowIntent(false); setChurnInput(""); setIntentForm({ target_sector: "", target_country: "", target_role: "" });
        }
    };

    return (
        <div className="mb-4 rounded-xl border border-white/8 bg-[var(--jr-surface)] p-4" data-testid="smart-hunts-row">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Sparkles size={14} className="text-[var(--jr-brand)]" />
                    <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{i18n.t("smarthunts.header")}</span>
                </div>
                <div className="flex items-center gap-1 bg-[var(--jr-surface-2)] rounded-full p-0.5 border border-white/10" data-testid="smart-hunt-source-toggle">
                    <button onClick={() => setSource("perplexity")}
                        data-testid="smart-hunt-src-perplexity"
                        className={`text-[10px] font-mono uppercase tracking-[0.16em] px-3 py-1 rounded-full transition-colors ${
                            source === "perplexity" ? "bg-[var(--jr-brand)] text-black" : "text-[var(--jr-text-2)] hover:text-white"
                        }`}>Perplexity</button>
                    <button onClick={() => setSource("serper")}
                        data-testid="smart-hunt-src-serper"
                        className={`text-[10px] font-mono uppercase tracking-[0.16em] px-3 py-1 rounded-full transition-colors ${
                            source === "serper" ? "bg-[#22c55e] text-black" : "text-[var(--jr-text-2)] hover:text-white"
                        }`}>Serper</button>
                </div>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
                {/* Look-alike */}
                <SmartHuntTile
                    testid="smart-hunt-look-alike"
                    color="#22c55e"
                    title={i18n.t("smarthunts.lookalike_title")}
                    desc={i18n.t("smarthunts.lookalike_desc")}
                    disabled={running === "look_alike"}
                    running={running === "look_alike"}
                    onClick={() => trigger("look-alike", {}, "Look-alike")}
                />
                {/* Buyer-intent (opens inline form) */}
                <div className="rounded-xl border border-white/10 bg-[var(--jr-surface-2)] p-3 flex flex-col gap-2" style={{ borderLeft: "3px solid #eab308" }}>
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <div className="text-sm font-medium text-white">Buyer-intent scanner</div>
                            <div className="text-[11px] text-[var(--jr-text-2)] mt-0.5">{i18n.t("smarthunts.intent_desc")}</div>
                        </div>
                        <span className="text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded" style={{ background: "#eab30822", color: "#eab308" }}>Weekly</span>
                    </div>
                    {showIntent ? (
                        <div className="space-y-2">
                            <input value={intentForm.target_sector} onChange={(e) => setIntentForm((f) => ({ ...f, target_sector: e.target.value }))}
                                data-testid="smart-hunt-intent-sector"
                                placeholder="Sector (bv. SaaS)" className="w-full text-xs bg-[var(--jr-surface)] border border-white/10 rounded px-2 py-1.5 text-white placeholder:text-[var(--jr-text-3)] focus:border-[var(--jr-brand)] outline-none" />
                            <div className="grid grid-cols-2 gap-2">
                                <input value={intentForm.target_country} onChange={(e) => setIntentForm((f) => ({ ...f, target_country: e.target.value }))}
                                    data-testid="smart-hunt-intent-country"
                                    placeholder="Land (BE)" className="text-xs bg-[var(--jr-surface)] border border-white/10 rounded px-2 py-1.5 text-white placeholder:text-[var(--jr-text-3)] focus:border-[var(--jr-brand)] outline-none" />
                                <input value={intentForm.target_role} onChange={(e) => setIntentForm((f) => ({ ...f, target_role: e.target.value }))}
                                    data-testid="smart-hunt-intent-role"
                                    placeholder={i18n.t("smarthunts.role_ph")} className="text-xs bg-[var(--jr-surface)] border border-white/10 rounded px-2 py-1.5 text-white placeholder:text-[var(--jr-text-3)] focus:border-[var(--jr-brand)] outline-none" />
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => trigger("buyer-intent", intentForm, "Buyer-intent")} disabled={running === "buyer_intent"}
                                    data-testid="smart-hunt-intent-run"
                                    className="flex-1 text-xs px-3 py-1.5 rounded-full bg-[#eab308] text-black font-medium hover:bg-[#eab308]/85 disabled:opacity-50">
                                    {running === "buyer_intent" ? i18n.t("smarthunts.busy") : i18n.t("smarthunts.start_scan")}
                                </button>
                                <button onClick={() => setShowIntent(false)} className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5">{i18n.t("common.cancel")}</button>
                            </div>
                        </div>
                    ) : (
                        <button onClick={() => setShowIntent(true)}
                            data-testid="smart-hunt-buyer-intent"
                            className="text-xs px-3 py-1.5 rounded-full bg-[#eab308]/15 text-[#eab308] border border-[#eab308]/40 hover:bg-[#eab308]/25 self-start">
                            {i18n.t("smarthunts.configure_scan")} →
                        </button>
                    )}
                </div>
                {/* Competitor-churn (opens inline form) */}
                <div className="rounded-xl border border-white/10 bg-[var(--jr-surface-2)] p-3 flex flex-col gap-2" style={{ borderLeft: "3px solid #ef4444" }}>
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <div className="text-sm font-medium text-white">Competitor-churn</div>
                            <div className="text-[11px] text-[var(--jr-text-2)] mt-0.5">{i18n.t("smarthunts.churn_desc")}</div>
                        </div>
                        <span className="text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded" style={{ background: "#ef444422", color: "#ef4444" }}>Hot lead</span>
                    </div>
                    {showChurn ? (
                        <div className="space-y-2">
                            <input value={churnInput} onChange={(e) => setChurnInput(e.target.value)}
                                data-testid="smart-hunt-churn-input"
                                placeholder={i18n.t("smarthunts.churn_ph")}
                                className="w-full text-xs bg-[var(--jr-surface)] border border-white/10 rounded px-2 py-1.5 text-white placeholder:text-[var(--jr-text-3)] focus:border-[var(--jr-brand)] outline-none" />
                            <div className="flex gap-2">
                                <button onClick={() => trigger("competitor-churn", { competitor_name: churnInput.trim() }, "Competitor-churn")}
                                    disabled={running === "competitor_churn" || !churnInput.trim()}
                                    data-testid="smart-hunt-churn-run"
                                    className="flex-1 text-xs px-3 py-1.5 rounded-full bg-[#ef4444] text-white font-medium hover:bg-[#ef4444]/85 disabled:opacity-50">
                                    {running === "competitor_churn" ? i18n.t("smarthunts.busy") : i18n.t("smarthunts.find_migrants")}
                                </button>
                                <button onClick={() => setShowChurn(false)} className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5">{i18n.t("common.cancel")}</button>
                            </div>
                        </div>
                    ) : (
                        <button onClick={() => setShowChurn(true)}
                            data-testid="smart-hunt-competitor-churn"
                            className="text-xs px-3 py-1.5 rounded-full bg-[#ef4444]/15 text-[#ef4444] border border-[#ef4444]/40 hover:bg-[#ef4444]/25 self-start">
                            Kies concurrent →
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function SmartHuntTile({ testid, color, title, desc, onClick, disabled, running }) {
    return (
        <button onClick={onClick} disabled={disabled}
            data-testid={testid}
            style={{ borderLeft: `3px solid ${color}` }}
            className="text-left rounded-xl border border-white/10 bg-[var(--jr-surface-2)] p-3 hover:border-white/25 transition-colors disabled:opacity-60 disabled:cursor-not-allowed group">
            <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium text-white">{title}</div>
                {running
                    ? <Loader2 size={14} className="animate-spin" style={{ color }} />
                    : <ArrowUpRight size={14} className="text-[var(--jr-text-3)] group-hover:text-white transition-colors" />}
            </div>
            <div className="text-[11px] text-[var(--jr-text-2)] mt-1">{desc}</div>
            <div className="text-[9px] font-mono uppercase tracking-[0.14em] mt-2 px-1.5 py-0.5 rounded inline-block" style={{ background: `${color}22`, color }}>
                {running ? i18n.t("smarthunts.busy") : i18n.t("smarthunts.one_click")}
            </div>
        </button>
    );
}
